<script type="text/jsx">
  import emitter from '../../mixins/emitter';
  import Menubar from '../../utils/menu/aria-menubar';
  import { addClass, removeClass, hasClass } from '../../utils/dom';

  export default {
    name: 'ZmMenu',

    render(h) {
      const component = (
        <ul
          role="menubar"
          key={ +this.collapse }
          style={{ backgroundColor: this.backgroundColor || '' }}
          class={{
            'zm-menu--horizontal': this.mode === 'horizontal',
            'zm-menu--collapse': this.collapse,
            'zm-menu': true
          }}
        >
          { this.$slots.default }
        </ul>
      );

      if (this.collapseTransition) {
        return (
          <zm-menu-collapse-transition>
            { component }
          </zm-menu-collapse-transition>
        );
      } else {
        return component;
      }
    },

    componentName: 'ZmMenu',

    mixins: [emitter],

    provide() {
      return {
        rootMenu: this
      };
    },

    components: {
      'zm-menu-collapse-transition': {
        functional: true,
        render(createElement, context) {
          const data = {
            props: {
              mode: 'out-in'
            },
            on: {
              beforeEnter(el) {
                el.style.opacity = 0.2;
              },

              enter(el) {
                addClass(el, 'zm-opacity-transition');
                el.style.opacity = 1;
              },

              afterEnter(el) {
                removeClass(el, 'zm-opacity-transition');
                el.style.opacity = '';
              },

              beforeLeave(el) {
                if (!el.dataset) el.dataset = {};

                if (hasClass(el, 'zm-menu--collapse')) {
                  removeClass(el, 'zm-menu--collapse');
                  el.dataset.oldOverflow = el.style.overflow;
                  el.dataset.scrollWidth = el.clientWidth;
                  addClass(el, 'zm-menu--collapse');
                } else {
                  addClass(el, 'zm-menu--collapse');
                  el.dataset.oldOverflow = el.style.overflow;
                  el.dataset.scrollWidth = el.clientWidth;
                  removeClass(el, 'zm-menu--collapse');
                }

                el.style.width = el.scrollWidth + 'px';
                el.style.overflow = 'hidden';
              },

              leave(el) {
                addClass(el, 'horizontal-collapse-transition');
                el.style.width = el.dataset.scrollWidth + 'px';
              }
            }
          };
          return createElement('transition', data, context.children);
        }
      }
    },

    props: {
      mode: {
        type: String,
        default: 'vertical'
      },
      defaultActive: {
        type: String,
        default: ''
      },
      defaultOpeneds: Array,
      uniqueOpened: Boolean,
      router: Boolean,
      menuTrigger: {
        type: String,
        default: 'hover'
      },
      collapse: Boolean,
      backgroundColor: String,
      textColor: String,
      activeTextColor: String,
      collapseTransition: {
        type: Boolean,
        default: true
      }
    },
    data() {
      return {
        activeIndex: this.defaultActive,
        openedMenus: (this.defaultOpeneds && !this.collapse) ? this.defaultOpeneds.slice(0) : [],
        items: {},
        submenus: {}
      };
    },
    computed: {
      hoverBackground() {
        return this.backgroundColor ? this.mixColor(this.backgroundColor, 0.2) : '';
      },
      isMenuPopup() {
        return this.mode === 'horizontal' || (this.mode === 'vertical' && this.collapse);
      }
    },
    watch: {
      defaultActive(value) {
        if (!this.items[value]) {
          this.activeIndex = null;
        }
        this.updateActiveIndex(value);
      },

      defaultOpeneds(value) {
        if (!this.collapse) {
          this.openedMenus = value;
        }
      },

      collapse(value) {
        if (value) this.openedMenus = [];
        this.broadcast('ZmSubmenu', 'toggle-collapse', value);
      }
    },
    methods: {
      updateActiveIndex(val) {
        const item = this.items[val] || this.items[this.activeIndex] || this.items[this.defaultActive];
        if (item) {
          this.activeIndex = item.index;
          this.initOpenedMenu();
        } else {
          this.activeIndex = null;
        }
      },

      getColorChannels(color) {
        color = color.replace('#', '');
        if (/^[0-9a-fA-F]{3}$/.test(color)) {
          color = color.split('');
          for (let i = 2; i >= 0; i--) {
            color.splice(i, 0, color[i]);
          }
          color = color.join('');
        }
        if (/^[0-9a-fA-F]{6}$/.test(color)) {
          return {
            red: parseInt(color.slice(0, 2), 16),
            green: parseInt(color.slice(2, 4), 16),
            blue: parseInt(color.slice(4, 6), 16)
          };
        } else {
          return {
            red: 255,
            green: 255,
            blue: 255
          };
        }
      },
      mixColor(color, percent) {
        let { red, green, blue } = this.getColorChannels(color);
        if (percent > 0) { // shade given color
          red *= 1 - percent;
          green *= 1 - percent;
          blue *= 1 - percent;
        } else { // tint given color
          red += (255 - red) * percent;
          green += (255 - green) * percent;
          blue += (255 - blue) * percent;
        }
        return `rgb(${ Math.round(red) }, ${ Math.round(green) }, ${ Math.round(blue) })`;
      },
      addItem(item) {
        this.$set(this.items, item.index, item);
      },
      removeItem(item) {
        delete this.items[item.index];
      },
      addSubmenu(item) {
        this.$set(this.submenus, item.index, item);
      },
      removeSubmenu(item) {
        delete this.submenus[item.index];
      },
      openMenu(index, indexPath) {
        let openedMenus = this.openedMenus;
        if (openedMenus.indexOf(index) !== -1) return;
        // 将不在该菜单路径下的其余菜单收起
        // collapse all menu that are not under current menu item
        if (this.uniqueOpened) {
          this.openedMenus = openedMenus.filter(index => {
            return indexPath.indexOf(index) !== -1;
          });
        }
        this.openedMenus.push(index);
      },
      closeMenu(index) {
        const i = this.openedMenus.indexOf(index);
        if (i !== -1) {
          this.openedMenus.splice(i, 1);
        }
      },
      handleSubmenuClick(submenu) {
        const { index, indexPath } = submenu;
        let isOpened = this.openedMenus.indexOf(index) !== -1;

        if (isOpened) {
          this.closeMenu(index);
          this.$emit('close', index, indexPath);
        } else {
          this.openMenu(index, indexPath);
          this.$emit('open', index, indexPath);
        }
      },
      handleItemClick(item) {
        const { index, indexPath } = item;
        const oldActiveIndex = this.activeIndex;
        const hasIndex = item.index !== null;

        if (hasIndex) {
          this.activeIndex = item.index;
        }

        this.$emit('select', index, indexPath, item);

        if (this.mode === 'horizontal' || this.collapse) {
          this.openedMenus = [];
        }

        if (this.router && hasIndex) {
          this.routeToItem(item, (error) => {
            this.activeIndex = oldActiveIndex;
            if (error) console.error(error);
          });
        }
      },
      // 初始化展开菜单
      // initialize opened menu
      initOpenedMenu() {
        const index = this.activeIndex;
        const activeItem = this.items[index];
        if (!activeItem || this.mode === 'horizontal' || this.collapse) return;

        let indexPath = activeItem.indexPath;

        // 展开该菜单项的路径上所有子菜单
        // expand all submenus of the menu item
        indexPath.forEach(index => {
          let submenu = this.submenus[index];
          submenu && this.openMenu(index, submenu.indexPath);
        });
      },
      routeToItem(item, onError) {
        let route = item.route || item.index;
        try {
          this.$router.push(route, () => {}, onError);
        } catch (e) {
          console.error(e);
        }
      },
      open(index) {
        const { indexPath } = this.submenus[index.toString()];
        indexPath.forEach(i => this.openMenu(i, indexPath));
      },
      close(index) {
        this.closeMenu(index);
      }
    },
    mounted() {
      this.initOpenedMenu();
      this.$on('item-click', this.handleItemClick);
      this.$on('submenu-click', this.handleSubmenuClick);
      if (this.mode === 'horizontal') {
        new Menubar(this.$el); // eslint-disable-line
      }
      this.$watch('items', this.updateActiveIndex);
    }
  };
</script>
<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/mixins/utils";
  @import "../../scss/common/var";
  @import "../../scss/common/transition";

  @mixin menu-item {
    height: 56px;
    line-height: 56px;
    font-size: $--font-size-base;
    color: $--color-text-primary;
    padding: 0 20px;
    list-style: none;
    cursor: pointer;
    position: relative;
    transition: border-color .3s, background-color .3s, color .3s;
    box-sizing: border-box;
    white-space: nowrap;

    * {
      vertical-align: middle;
    }

    i {
      color: $--color-text-secondary;
    }

    &:hover,
    &:focus {
      outline: none;
      background-color: #ecf5ff;
    }

    @include when(disabled) {
      opacity: 0.25;
      cursor: not-allowed;
      background: none !important;
    }
  }

  @include b(menu) {
    border-right: solid 1px #e6e6e6;
    list-style: none;
    position: relative;
    margin: 0;
    padding-left: 0;
    background-color: $--color-white;
    @include utils-clearfix;
    &.zm-menu--horizontal {
      border-bottom: solid 1px #e6e6e6;
    }

    @include m(horizontal) {
      border-right: none;
      & > .zm-menu-item {
        float: left;
        height: 60px;
        line-height: 60px;
        margin: 0;
        border-bottom: 2px solid transparent;
        color: $--color-text-secondary;

        a,
        a:hover {
          color: inherit;
        }

        &:not(.is-disabled):hover,
        &:not(.is-disabled):focus{
          background-color: #fff;
        }
      }
      & > .zm-submenu {
        float: left;

        &:focus,
        &:hover {
          outline: none;
          .zm-submenu__title {
            color: $--color-text-primary;
          }
        }

        &.is-active {
          .zm-submenu__title {
            border-bottom: 2px solid $--color-primary;
            color: $--color-text-primary;
          }
        }

        & .zm-submenu__title {
          height: 60px;
          line-height: 60px;
          border-bottom: 2px solid transparent;
          color: $--color-text-secondary;

          &:hover {
            background-color: #fff;
          }
        }
        & .zm-submenu__icon-arrow {
          position: static;
          vertical-align: middle;
          margin-left: 8px;
          margin-top: -3px;
        }
      }
      & .zm-menu {
        & .zm-menu-item,
        & .zm-submenu__title {
          // background-color: $--color-white;
          float: none;
          height: 36px;
          line-height: 36px;
          padding: 0 10px;
          color: $--color-text-secondary;
        }
        & .zm-menu-item.is-active,
        & .zm-submenu.is-active > .zm-submenu__title {
          color: $--color-text-primary;
        }
      }
      & .zm-menu-item:not(.is-disabled):hover,
      & .zm-menu-item:not(.is-disabled):focus {
        outline: none;
        color: $--color-text-primary;
      }
      & > .zm-menu-item.is-active {
        border-bottom: 2px solid $--color-primary;
        color: $--color-text-primary;
      }
    }
    @include m(collapse) {
      width: 64px;

      > .zm-menu-item,
      > .zm-submenu > .zm-submenu__title {
        [class^="zm-icon-"] {
          margin: 0;
          vertical-align: middle;
          width: 24px;
          text-align: center;
        }
        .zm-submenu__icon-arrow {
          display: none;
        }
        span {
          height: 0;
          width: 0;
          overflow: hidden;
          visibility: hidden;
          display: inline-block;
        }
      }

      > .zm-menu-item.is-active i {
        color: inherit;
      }

      .zm-menu .zm-submenu {
        min-width: 200px;
      }

      .zm-submenu {
        position: relative;
        & .zm-menu {
          position: absolute;
          margin-left: 5px;
          top: 0;
          left: 100%;
          z-index: 10;
          border: 1px solid $--border-color-light;
          border-radius: $--border-radius-small;
          box-shadow: $--box-shadow-light;
        }

        &.is-opened {
          > .zm-submenu__title .zm-submenu__icon-arrow {
            transform: none;
          }
        }
      }
    }
    @include m(popup) {
      z-index: 100;
      min-width: 200px;
      border: none;
      padding: 5px 0;
      border-radius: $--border-radius-small;
      box-shadow: $--box-shadow-light;

      &-bottom-start {
        margin-top: 5px;
      }
      &-right-start {
        margin-left: 5px;
        margin-right: 5px;
      }
    }
  }
  @include b(menu-item) {
    @include menu-item;

    & [class^="zm-icon-"] {
      margin-right: 5px;
      width: 24px;
      text-align: center;
      font-size: 18px;
      vertical-align: middle;
    }
    @include when(active) {
      color: $--color-primary;
      i {
        color: inherit;
      }
    }
  }

  @include b(submenu) {
    list-style: none;
    margin: 0;
    padding-left: 0;

    @include e(title) {
      @include menu-item;

      &:hover {
        background-color: #ecf5ff;
      }
    }
    & .zm-menu {
      border: none;
    }
    & .zm-menu-item {
      height: 50px;
      line-height: 50px;
      padding: 0 45px;
      min-width: 200px;
    }
    @include e(icon-arrow) {
      position: absolute;
      top: 50%;
      right: 20px;
      margin-top: -7px;
      transition: transform .3s;
      font-size: 12px;
    }
    & [class*=" zm-icon-"],
    & [class^=zm-icon-] {
      font-style:normal;
      font-weight:400;
      font-variant:normal;
      text-transform:none;
      line-height:1;
      vertical-align:baseline;
      display:inline-block;
    }
    @include when(active) {
      .zm-submenu__title {
        border-bottom-color: $--color-primary;
      }
    }
    @include when(opened) {
      > .zm-submenu__title .zm-submenu__icon-arrow {
        transform: rotateZ(180deg);
      }
    }
    @include when(disabled) {
      .zm-submenu__title,
      .zm-menu-item {
        opacity: 0.25;
        cursor: not-allowed;
        background: none !important;
      }
    }
    [class^="zm-icon-"] {
      vertical-align: middle;
      margin-right: 5px;
      width: 24px;
      text-align: center;
      font-size: 18px;
    }
  }

  @include b(menu-item-group) {
    > ul {
      padding: 0;
    }
    @include e(title) {
      padding: 7px 0 7px 20px;
      line-height: normal;
      font-size: 12px;
      color: $--color-text-secondary;
    }
  }

  .horizontal-collapse-transition .zm-submenu__title .zm-submenu__icon-arrow {
    transition: .2s;
    opacity: 0;
  }
  .zm-theme--dark {
    @include b(menu) {
      background-color: #131619;
      @include m(horizontal){
        & > .zm-menu-item.is-active{
          color: #0E71EB;
          background-color: #252A30;
        }
        & .zm-menu {
          & .zm-menu-item,
          & .zm-submenu__title {
            color: #fff;
          }
        }
      }
    }
    @include b(menu-item){
      color: #fff;
      background-color: transparent;
      &:not(.is-disabled):hover,
      &:not(.is-disabled):focus{
        background-color: #252A30;
        color: #fff;
      }

    }
    @include b(submenu){
      @include e(title){
        color: #fff;
        background-color: transparent;
        i{
          color: #fff;
        }
        &:hover{
          background-color: #252A30;
          color: #fff;
        }
      }
      @include when(active){
        @include e(title){
          color: #0E71EB;
          i{
            color: #0E71EB;
          }
        }
      }
    }
  }
</style>
